// Quote types for frontend

export enum QuoteStatus {
  DRAFT = 'draft',
  READY = 'ready',
  SENT = 'sent',
  VIEWED = 'viewed',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  CONVERTED = 'converted',
}

export enum PropertyType {
  HOUSE = 'house',
  APARTMENT = 'apartment',
  CONDO = 'condo',
  OFFICE = 'office',
  COMMERCIAL = 'commercial',
}

export enum RoomType {
  LIVING_ROOM = 'living_room',
  BEDROOM = 'bedroom',
  MASTER_BEDROOM = 'master_bedroom',
  GUEST_BEDROOM = 'guest_bedroom',
  DINING = 'dining',
  KITCHEN = 'kitchen',
  OFFICE = 'office',
  BATHROOM = 'bathroom',
  FOYER = 'foyer',
  HALLWAY = 'hallway',
  OTHER = 'other',
}

export enum CropType {
  RECTANGLE = 'rectangle',
  POLYGON = 'polygon',
}

export enum RenderStatus {
  PENDING = 'pending',
  GENERATING = 'generating',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum RenderGenerationStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface AssetTemplate {
  id: string;
  name: string;
  description?: string;
  base_price?: number;
  category?: { id: string; name: string };
  images?: Array<{ id: string; url: string }>;
}

export interface QuoteFloorPlan {
  id: string;
  quote_id: string;
  floor_number: number;
  name?: string;
  image_url?: string;
  storage_key?: string;
  scale_pixels_per_foot?: number;
  created_at: string;
  updated_at: string;
  rooms_count?: number;
}

export interface QuoteRoomRender {
  id: string;
  quote_room_id: string;
  image_url?: string;
  storage_key?: string;
  prompt_used?: string;
  generation_params?: RestyleGenerationParams;
  replicate_prediction_id?: string;
  status: RenderGenerationStatus;
  error_message?: string;
  generation_time_ms?: number;
  is_selected: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuoteItem {
  id: string;
  quote_room_id: string;
  asset_template_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  placement_notes?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  asset_template?: AssetTemplate;
}

export interface RectangleCropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PolygonCropData {
  points: Array<{ x: number; y: number }>;
}

export interface QuoteRoom {
  id: string;
  quote_id: string;
  floor_plan_id?: string;
  room_type: RoomType;
  room_name?: string;
  crop_type?: CropType;
  crop_data?: RectangleCropData | PolygonCropData | RestyleMaskData;
  source_image_url?: string;
  mask_image_url?: string;
  mask_storage_key?: string;
  cropped_image_url?: string;
  cropped_storage_key?: string;
  original_image_url?: string;
  width_ft?: number;
  length_ft?: number;
  area_sqft?: number;
  budget?: number;
  render_status?: RenderStatus;
  render_error?: string;
  selected_render_id?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  floor_plan?: QuoteFloorPlan;
  items?: QuoteItem[];
  renders?: QuoteRoomRender[];
  selected_render?: QuoteRoomRender;
  items_count?: number;
  items_total?: number;
  renders_count?: number;
}

export interface Quote {
  id: string;
  quote_number: string;
  status: QuoteStatus;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_company?: string;
  property_name?: string;
  property_address?: string;
  property_type?: PropertyType;
  total_sqft?: number;
  bedrooms?: number;
  rental_length_months: number;
  total_budget?: number;
  valid_until?: string;
  sent_at?: string;
  viewed_at?: string;
  responded_at?: string;
  converted_to_order_id?: string;
  converted_at?: string;
  rejection_reason?: string;
  notes?: string;
  share_token?: string;
  created_by_user_id: string;
  assigned_to_user_id?: string;
  created_at: string;
  updated_at: string;
  created_by_user?: User;
  assigned_to_user?: User;
  floor_plans?: QuoteFloorPlan[];
  rooms?: QuoteRoom[];
  total_items_count?: number;
  total_rooms_count?: number;
  monthly_total?: number;
  total_price?: number;
}

export interface GenerationParams {
  style?: string[];
  lighting?: string;
  camera_angle?: string;
  floor_type?: string;
  wall_color?: string;
  custom_instructions?: string;
  resolution?: string;
}

export enum QuoteType {
  STANDARD = 'standard',
  RESTYLE = 'restyle',
}

export interface RestyleAreaInput {
  id: string;
  color: string;
  asset_template_id?: string;
  center_x_pct: number;
  center_y_pct: number;
  position_label: string;
}

export interface RestyleMaskData {
  placement_mode: 'auto' | 'manual';
  areas: RestyleAreaInput[];
  original_storage_key?: string;
  editor_base_render_id?: string;
}

export interface RestyleGenerationParams extends GenerationParams {
  placement_mode?: 'auto' | 'manual';
  areas?: RestyleAreaInput[];
  editor_base_render_id?: string;
}

export interface RestyleProject {
  id: string;
  quote_number: string;
  type?: QuoteType;
  property_name?: string | null;
  property_address?: string | null;
  created_at: string;
  restyle_converted_at?: string | null;
  room?: QuoteRoom | null;
  thumbnail_url?: string;
  renders_count?: number;
}

export interface FurnitureRecommendation {
  asset_template_id: string;
  asset_template: AssetTemplate;
  score: number;
  reasoning: string;
  suggested_quantity: number;
}

export interface RecommendationPackage {
  tier: 'budget' | 'standard' | 'premium';
  total_price: number;
  items: FurnitureRecommendation[];
}

export interface QuoteStats {
  total: number;
  draft: number;
  ready: number;
  sent: number;
  viewed: number;
  accepted: number;
  rejected: number;
  expired: number;
  converted: number;
  total_value: number;
  avg_value: number;
}

// Helper functions
export function getQuoteStatusLabel(status: QuoteStatus): string {
  const labels: Record<QuoteStatus, string> = {
    [QuoteStatus.DRAFT]: 'Draft',
    [QuoteStatus.READY]: 'Ready',
    [QuoteStatus.SENT]: 'Sent',
    [QuoteStatus.VIEWED]: 'Viewed',
    [QuoteStatus.ACCEPTED]: 'Accepted',
    [QuoteStatus.REJECTED]: 'Rejected',
    [QuoteStatus.EXPIRED]: 'Expired',
    [QuoteStatus.CONVERTED]: 'Converted',
  };
  return labels[status] || status;
}

export function getRoomTypeLabel(type: RoomType): string {
  const labels: Record<RoomType, string> = {
    [RoomType.LIVING_ROOM]: 'Living Room',
    [RoomType.BEDROOM]: 'Bedroom',
    [RoomType.MASTER_BEDROOM]: 'Master Bedroom',
    [RoomType.GUEST_BEDROOM]: 'Guest Bedroom',
    [RoomType.DINING]: 'Dining Room',
    [RoomType.KITCHEN]: 'Kitchen',
    [RoomType.OFFICE]: 'Office',
    [RoomType.BATHROOM]: 'Bathroom',
    [RoomType.FOYER]: 'Foyer',
    [RoomType.HALLWAY]: 'Hallway',
    [RoomType.OTHER]: 'Other',
  };
  return labels[type] || type;
}

export function getPropertyTypeLabel(type: PropertyType): string {
  const labels: Record<PropertyType, string> = {
    [PropertyType.HOUSE]: 'House',
    [PropertyType.APARTMENT]: 'Apartment',
    [PropertyType.CONDO]: 'Condo',
    [PropertyType.OFFICE]: 'Office',
    [PropertyType.COMMERCIAL]: 'Commercial',
  };
  return labels[type] || type;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
}
